import { useEffect, useState } from "react";
import { Loader2, X, Plus, Pencil, Layers } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtErr } from "../api";

// Departments — backed by the existing attendance API (/api/departments).
// The departments API supports create + rename/re-parent (no active flag) — deletion stays unavailable here.
const Departments = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | dept object
  const [form, setForm] = useState({ name: "", parent_id: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await staffApi.get("/departments");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", parent_id: "" }); setFormError(""); setEditing("new"); };
  const openEdit = (d) => { setForm({ name: d.name || "", parent_id: d.parent_id || "" }); setFormError(""); setEditing(d); };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), parent_id: form.parent_id || null };
      if (editing === "new") {
        await staffApi.post("/departments", payload);
        toast.success("Department added.");
      } else {
        await staffApi.patch(`/departments/${editing.id}`, payload);
        toast.success("Department updated.");
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="departments-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Departments</h1>
          <p className="mt-1 text-sm text-slate-500">Department structure shared across HRMS and attendance.</p>
        </div>
        <button onClick={openNew} data-testid="dept-add-btn"
          className="inline-flex items-center gap-1.5 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
          <Plus size={13} /> Add Department
        </button>
      </div>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="dept-error">{error}</p>
      ) : rows.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="dept-empty">No departments yet.</p>
      ) : (
        <div className="mt-6 bg-white border border-slate-200" data-testid="dept-table">
          <ul className="divide-y divide-slate-100" data-testid="dept-list">
            {rows.map((d) => (
              <li key={d.id} className="px-5 py-3.5 flex items-center justify-between gap-4" data-testid={`dept-row-${d.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 shrink-0 bg-[#0A1128] text-white flex items-center justify-center">
                    <Layers size={14} className="text-[#E8574F]" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{d.name}</p>
                    <p className="text-xs text-slate-400">
                      {d.parent_name ? `Under ${d.parent_name} · ` : ""}Created {fmtDate(d.created_at)}
                    </p>
                  </div>
                </div>
                <button onClick={() => openEdit(d)} data-testid={`dept-edit-${d.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A1128] hover:underline shrink-0">
                  <Pencil size={11} /> Edit
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="dept-form-modal" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">{editing === "new" ? "Add Department" : `Edit ${editing.name}`}</h2>
              <button onClick={() => setEditing(null)} data-testid="dept-form-close" className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Name *</label>
                <input required data-testid="dept-form-name" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Parent Department</label>
                <select data-testid="dept-form-parent" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}>
                  <option value="">No parent (top level)</option>
                  {rows.filter((d) => editing === "new" || d.id !== editing.id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="dept-form-error">{formError}</p>}
              <button type="submit" disabled={busy} data-testid="dept-form-submit"
                className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} {editing === "new" ? "Add Department" : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Departments;
